import React from 'react';
import { Box, Typography, Button, Container, Paper } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
        };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to console for debugging
        console.error('ErrorBoundary caught an error:', error, errorInfo);

        this.setState({
            error,
            errorInfo,
        });

        // Optional: Log to error reporting service
        // logErrorToService(error, errorInfo);
    }

    handleReset = () => {
        // Clear error state and reload
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });

        // Reload the page to reset the app
        window.location.href = '/shop';
    };

    handleReload = () => {
        window.location.reload();
    };

    render() {
        if (this.state.hasError) {
            return (
                <Container maxWidth="md">
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minHeight: '100vh',
                            py: 4,
                        }}
                    >
                        <Paper
                            elevation={3}
                            sx={{
                                p: 4,
                                textAlign: 'center',
                                backgroundColor: '#fff',
                                borderRadius: 2,
                                maxWidth: '600px',
                            }}
                        >
                            <ErrorOutlineIcon
                                sx={{
                                    fontSize: 80,
                                    color: '#ff5e1c',
                                    mb: 2,
                                }}
                            />
                            <Typography
                                variant="h4"
                                gutterBottom
                                sx={{
                                    fontWeight: 600,
                                    color: '#333',
                                    mb: 2,
                                }}
                            >
                                Oops! Something went wrong
                            </Typography>
                            <Typography
                                variant="body1"
                                color="text.secondary"
                                sx={{ mb: 3 }}
                            >
                                We encountered an unexpected error. This has been logged and we'll look into it.
                            </Typography>

                            {process.env.NODE_ENV === 'development' && this.state.error && (
                                <Box
                                    sx={{
                                        mt: 3,
                                        p: 2,
                                        backgroundColor: '#f5f5f5',
                                        borderRadius: 1,
                                        textAlign: 'left',
                                        overflow: 'auto',
                                        maxHeight: '200px',
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        component="pre"
                                        sx={{
                                            fontSize: '12px',
                                            fontFamily: 'monospace',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {this.state.error.toString()}
                                        {this.state.errorInfo && this.state.errorInfo.componentStack}
                                    </Typography>
                                </Box>
                            )}

                            <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center' }}>
                                <Button
                                    variant="contained"
                                    onClick={this.handleReset}
                                    sx={{
                                        backgroundColor: '#ff5e1c',
                                        '&:hover': {
                                            backgroundColor: '#e65419',
                                        },
                                        px: 4,
                                    }}
                                >
                                    Go to Shop
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={this.handleReload}
                                    sx={{
                                        borderColor: '#ff5e1c',
                                        color: '#ff5e1c',
                                        '&:hover': {
                                            borderColor: '#e65419',
                                            backgroundColor: 'rgba(255, 94, 28, 0.04)',
                                        },
                                        px: 4,
                                    }}
                                >
                                    Reload Page
                                </Button>
                            </Box>
                        </Paper>
                    </Box>
                </Container>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
